import React from 'react';
import { FaStop } from 'react-icons/fa';
import TimerControls from './TimerControls';

interface SessionTimeDisplayProps {
  isSessionActive: boolean;
  sessionDuration: number;
  timeRemaining: number;
  wordCount: number;
  wordGoal: number;
  isWordGoalMode: boolean;
  onResetSession: () => void;
  showSessionConfigurator: boolean;
  onToggleShowSessionConfigurator: () => void;
  onStartSession: (duration: number, isWordGoal: boolean, goalTarget?: number) => void;
}

const SessionTimeDisplay: React.FC<SessionTimeDisplayProps> = ({
  isSessionActive,
  sessionDuration,
  timeRemaining,
  wordCount,
  wordGoal,
  isWordGoalMode,
  onResetSession,
  showSessionConfigurator,
  onToggleShowSessionConfigurator,
  onStartSession
}) => {
  // Format time for display (minutes:seconds)
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Calculate progress percentage
  const calculateProgress = (): number => {
    if (isWordGoalMode) {
      return Math.min(100, (wordCount / wordGoal) * 100);
    } else if (sessionDuration > 0) {
      return 100 - ((timeRemaining / (sessionDuration * 60)) * 100);
    }
    return 0;
  };

  // Handle reset session with focus management
  const handleResetSession = () => {
    onResetSession();
    // Return focus to text area after session ends
    setTimeout(() => {
      const textArea = document.querySelector('.editor-container textarea') as HTMLTextAreaElement;
      if (textArea) {
        textArea.focus();
      }
    }, 10);
  };

  if (!isSessionActive) {
    return (
      <div className="text-center text-gray-500 dark:text-gray-400 py-4 space-y-4">
        <TimerControls 
          onStartSession={onStartSession} 
          isSessionActive={isSessionActive}
          sessionDuration={0}
          timeRemaining={0}
          wordCount={0}
          wordGoal={0}
          isWordGoalMode={false}
          onResetSession={() => {}}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Active Session Display */}
      <div className="relative h-5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`absolute top-0 left-0 h-full transition-all duration-1000 ease-linear
            ${isWordGoalMode 
              ? 'bg-primary' 
              : 'bg-gradient-to-r from-primary to-green-400'
            }`}
          style={{ width: `${calculateProgress()}%` }}
        ></div>
      </div>
      
      <div className="flex justify-between items-center">
        <div className="text-xl font-mono text-primary font-semibold">
          {isWordGoalMode 
            ? `${wordCount}/${wordGoal} words` 
            : formatTime(timeRemaining)
          }
        </div>
        
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {isWordGoalMode 
            ? `${Math.round(calculateProgress())}% complete` 
            : `${Math.round(calculateProgress())}% elapsed`
          }
        </div>
      </div>
      
      <button
        onClick={handleResetSession}
        className="w-full flex items-center justify-center bg-red-500 hover:bg-red-600
          text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200
          focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50
          hover-lift button-press end-session-btn"
      >
        <FaStop className="mr-2" />
        <span>End Session</span>
      </button>
    </div>
  );
};

export default SessionTimeDisplay; 